import type { InitOutput } from "../wasm/vault-crypto/vault_crypto_wasm";

/**
 * Threshold ElGamal over Ristretto255 — the cryptographic core of the vault unlock
 * protocol (`NEW_VAULT.md`, Phase 3). All scalars and points cross the WASM boundary as
 * opaque base64url strings; callers should treat them as opaque tokens and never attempt
 * to parse them.
 *
 * Lifecycle:
 *   1. Setup (vault owner): `vaultKeygen()` → `{ secretScalar, publicKey }`. Wrap the MK
 *      with `elgamalWrap(mk, publicKey)` → `{ c1, c2 }`. Split `secretScalar` with
 *      `scalarSplit(secretScalar, t, n)` → shards. Store `c1`/`c2` on the platform; lock
 *      each shard with a per-trustee Trustee_Secret.
 *   2. Unlock (trustee): `partialDecrypt(shard, c1)` → partial point.
 *   3. Aggregate (executor): `aggregateAndUnwrap(partials, c2)` → raw MK bytes.
 */

let initPromise: Promise<InitOutput> | null = null;
async function ensureReady() {
  if (!initPromise) {
    const mod = await import("../wasm/vault-crypto/vault_crypto_wasm");
    const wasmPath = new URL(
      "../wasm/vault-crypto/vault_crypto_wasm_bg.wasm",
      import.meta.url
    );
    const bytes = await fetch(wasmPath).then((r) => r.arrayBuffer());
    initPromise = Promise.resolve(mod.initSync({ module: bytes }));
  }
  return initPromise;
}

export interface VaultKeypair {
  /** base64url Ristretto255 scalar `x` — the vault secret to be Shamir-shared. */
  secretScalar: string;
  /** base64url compressed Ristretto255 point `Y = x·G`. */
  publicKey: string;
}

export interface ElGamalCiphertext {
  /** base64url ephemeral point `C1`. */
  c1: string;
  /** base64url `nonce || AES-GCM(MK)` ciphertext `C2`. */
  c2: string;
}

export interface ScalarShard {
  /** 1-based Lagrange evaluation index. */
  index: number;
  /** base64url Ristretto255 scalar `f(index)`. */
  shard: string;
}

/** Generates a fresh vault keypair. */
export async function vaultKeygen(): Promise<VaultKeypair> {
  await ensureReady();
  const { vault_keygen } = await import(
    "../wasm/vault-crypto/vault_crypto_wasm"
  );
  return JSON.parse(vault_keygen()) as VaultKeypair;
}

/** Wraps a raw 32-byte master key under the vault public key (hashed-ElGamal). */
export async function elgamalWrap(
  masterKey: Uint8Array,
  publicKeyB64: string
): Promise<ElGamalCiphertext> {
  await ensureReady();
  const { elgamal_wrap } = await import(
    "../wasm/vault-crypto/vault_crypto_wasm"
  );
  return JSON.parse(elgamal_wrap(masterKey, publicKeyB64)) as ElGamalCiphertext;
}

/** Shamir-shares the vault secret scalar in the scalar field. */
export async function scalarSplit(
  secretScalarB64: string,
  threshold: number,
  sharesCount: number
): Promise<ScalarShard[]> {
  await ensureReady();
  const { scalar_split } = await import(
    "../wasm/vault-crypto/vault_crypto_wasm"
  );
  return JSON.parse(
    scalar_split(secretScalarB64, threshold, sharesCount)
  ) as ScalarShard[];
}

/** Computes a trustee's partial decryption `P_i = x_i · C1`. */
export async function partialDecrypt(
  shardB64: string,
  c1B64: string
): Promise<string> {
  await ensureReady();
  const { partial_decrypt } = await import(
    "../wasm/vault-crypto/vault_crypto_wasm"
  );
  return partial_decrypt(shardB64, c1B64);
}

export interface PartialPoint {
  index: number;
  partial: string;
}

/**
 * Aggregates a threshold of partial decryptions and unwraps the master key.
 * Throws if the partials are insufficient or inconsistent (AES-GCM auth failure).
 */
export async function aggregateAndUnwrap(
  partials: PartialPoint[],
  c2B64: string
): Promise<Uint8Array> {
  await ensureReady();
  const { aggregate_and_unwrap } = await import(
    "../wasm/vault-crypto/vault_crypto_wasm"
  );
  return aggregate_and_unwrap(JSON.stringify(partials), c2B64);
}
